import { apiRequest } from './http';
import type {
  ApiItemResponse,
  ApiListResponse,
  GovernanceLifecycleState,
  ValidationRuleRecord,
  ValidationRuleVersionRecord,
} from '../types/api';
import type { LifecycleAction } from './mappings';

type Query = Record<string, string | number | boolean | undefined>;

const actionState: Record<LifecycleAction, GovernanceLifecycleState> = {
  validate: 'VALIDATED',
  approve: 'APPROVED',
  activate: 'ACTIVE',
  reject: 'DRAFT',
};

function queryString(query: Query = {}): string {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.set(key, String(value));
  });
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

export const validationRulesApi = {
  list: (query: Query = {}) => apiRequest<ApiListResponse<ValidationRuleRecord>>(`/validation-rules${queryString(query)}`),
  getValidationRule: async (id: string) => (await apiRequest<ApiItemResponse<ValidationRuleRecord>>(`/validation-rules/${id}`)).data,
  createValidationRule: async (payload: Omit<ValidationRuleRecord, 'id' | 'createdAt' | 'updatedAt'>) =>
    (await apiRequest<ApiItemResponse<ValidationRuleRecord>>('/validation-rules', { method: 'POST', body: JSON.stringify(payload) })).data,
  deleteValidationRule: async (id: string) => apiRequest<void>(`/validation-rules/${id}`, { method: 'DELETE' }),
  listValidationRuleVersions: async (id: string) =>
    (await apiRequest<ApiItemResponse<ValidationRuleVersionRecord[]>>(`/validation-rules/${id}/versions`)).data,
  createValidationRuleVersion: async (
    id: string,
    payload: { ruleDefinition: Record<string, unknown>; severity?: 'error' | 'warning'; ruleType?: 'structure' | 'semantic'; changeLog?: string; versionLabel?: string },
  ) =>
    (await apiRequest<ApiItemResponse<ValidationRuleRecord>>(`/validation-rules/${id}/versions`, {
      method: 'POST',
      body: JSON.stringify({
        versionLabel: payload.versionLabel,
        severity: payload.severity,
        ruleType: payload.ruleType,
        ruleDefinition: payload.ruleDefinition,
        lifecycleState: 'DRAFT',
      }),
    })).data,
  updateValidationRuleVersionLifecycle: async (id: string, versionId: string, action: LifecycleAction) =>
    (await apiRequest<ApiItemResponse<ValidationRuleRecord>>(`/validation-rules/${id}/versions/${versionId}/lifecycle`, {
      method: 'PATCH',
      body: JSON.stringify({ lifecycleState: actionState[action] }),
    })).data,
};
